import {
  getAccessToken,
  storeAuth,
  refreshToken as apiRefreshToken,
  logout,
} from "../repository/authRepository";

let autoRefreshTimer: ReturnType<typeof setInterval> | null = null;

export function isTokenExpired(bufferSeconds: number = 60): boolean {
  const token = getAccessToken();
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expiresAt = payload.exp * 1000;
    return Date.now() >= expiresAt - bufferSeconds * 1000;
  } catch {
    return true;
  }
}

export async function refreshAccessToken(): Promise<string | null> {
  const storedRefresh = localStorage.getItem("refresh_token");
  if (!storedRefresh) return null;

  try {
    const response = await apiRefreshToken(storedRefresh);
    storeAuth(response);
    return response.access_token;
  } catch {
    logout();
    return null;
  }
}

export async function fetchWithAuth(
  url: string,
  options?: RequestInit
): Promise<Response> {
  if (isTokenExpired()) {
    await refreshAccessToken();
  }

  const token = getAccessToken();
  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers["Authorization"] = `Bearer ${newToken}`;
      response = await fetch(url, { ...options, headers });
    }
  }

  return response;
}

export function setupAutoRefresh(): void {
  clearAutoRefresh();
  // Check every 4 minutes; refresh if expiring within 60s
  autoRefreshTimer = setInterval(() => {
    if (isTokenExpired()) {
      refreshAccessToken();
    }
  }, 4 * 60 * 1000);
}

export function clearAutoRefresh(): void {
  if (autoRefreshTimer) {
    clearInterval(autoRefreshTimer);
    autoRefreshTimer = null;
  }
}
